import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import {
  AudioVideoProvider,
  RosterProvider,
  RemoteVideoTileProvider,
  ContentShareProvider,
  NotificationProvider,
  LoggerProvider,
} from "amazon-chime-sdk-component-library-react";
import { AppStateProvider } from "../features/video/providers/AppStateProvider";
import { ErrorProvider } from "../features/video/providers/ErrorProvider";
import { meetingConfig } from "../features/video/meetingConfig.ts";
import { Notifications } from "../features/video/containers/Notifications";
import { MeetingEventObserver } from "../features/video/components/MeetingEventObserver";
import { ChimeFeatureWrappers } from "../features/video/components/ChimeFeatureWrappers";
import { DevicesProvider } from "../features/video/providers/DevicesProvider";
import { VideoProvider } from "../features/video/providers/VideoContext";
import { VideoTileGridProvider } from "@platform/features/video/providers/VideoTileGridProvider/index.tsx";
import { VideoUIDialogs } from "../features/videoUI/components/VideoUIDialogs/VideoUIDialogs";
import { TrackingProvider } from "@shared/features/tracking/TrackingContext";
import { TranscriptionProvider } from "../features/video/providers/TranscriptionProvider";
import { VideoEventListeners } from "../features/video/VideoEventListeners";
import { ChatSessionContainer } from "../features/chat/components/ChatSession/ChatSessionContainer.tsx";
import {
  stopAllTrackedStreams,
  forceStopAllMediaStreams,
} from "../features/video/utils/stopChimeStreams";
import DashboardHeaderContainer from "@platform/features/dashboard/components/DashboardHeader/DashboardHeaderContainer.tsx";
import { DashboardNavigationItemsContainer } from "@platform/features/dashboard/components/DashboardNavigationItems/DashboardNavigationItemsContainer.tsx";
import { DashboardAlertBannerContainer } from "@platform/features/dashboard/components/DashboardAlertBanner/DashboardAlertBannerContainer.tsx";
import { ChatSheetContainer } from "../features/chat/components/ChatSheet/ChatSheetContainer.tsx";
import { useGetUserData } from "@platform/hooks/useGetUserData.ts";
import { usePlatform } from "@platform/PlatformContext";
import { useClosePlatformAccess } from "@platform/hooks/useClosePlatformAccess";

export type Props = {
  isWithNavigation?: boolean;
};

export function VideoLayout({ isWithNavigation = false }: Props) {
  useClosePlatformAccess();
  const { isTranscriptionDisabled } = usePlatform();

  // Clean up media streams when navigating away from video pages
  useEffect(() => {
    return () => {
      // Clean up when component unmounts (navigating away)
      stopAllTrackedStreams();
      forceStopAllMediaStreams();
    };
  }, []);

  useGetUserData({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return (
    <TrackingProvider
      debug={{ enabled: false }}
      services={{ posthog: true, google_analytics: true }}
    >
      <LoggerProvider logger={meetingConfig.logger}>
        <AppStateProvider>
          <ChimeFeatureWrappers>
            <ErrorProvider>
              <NotificationProvider>
                <AudioVideoProvider>
                  <DevicesProvider>
                    <VideoProvider>
                      <VideoTileGridProvider>
                        <RosterProvider>
                          <TranscriptionProvider
                            isTranscriptionDisabled={
                              isTranscriptionDisabled || false
                            }
                          >
                            <RemoteVideoTileProvider>
                              <ContentShareProvider>
                                <ChatSessionContainer>
                                  <Notifications />
                                  <MeetingEventObserver />
                                  <VideoEventListeners />
                                  {isWithNavigation ? (
                                    <div className="min-h-[100dvh] flex flex-col bg-brand-50 text-brand-800">
                                      <DashboardHeaderContainer>
                                        <DashboardNavigationItemsContainer />
                                      </DashboardHeaderContainer>
                                      <DashboardAlertBannerContainer />
                                      <main className="flex flex-1 h-full justify-center items-center">
                                        <Outlet />
                                      </main>
                                    </div>
                                  ) : (
                                    <main className="flex-grow flex flex-col bg-brand-50 h-full min-h-[100dvh]">
                                      <Outlet />
                                    </main>
                                  )}
                                  <VideoUIDialogs />
                                  <ChatSheetContainer />
                                </ChatSessionContainer>
                              </ContentShareProvider>
                            </RemoteVideoTileProvider>
                          </TranscriptionProvider>
                        </RosterProvider>
                      </VideoTileGridProvider>
                    </VideoProvider>
                  </DevicesProvider>
                </AudioVideoProvider>
              </NotificationProvider>
            </ErrorProvider>
          </ChimeFeatureWrappers>
        </AppStateProvider>
      </LoggerProvider>
    </TrackingProvider>
  );
}

export default VideoLayout;
